import {
  Card,
  CardHeader,
  Grid,
  Stack,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import AutoComplete from '../../../components/AutoComplete';
import { useAppDispatch, useAppSelector } from '../../../store';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CreatePatchAdvertise,
} from '../../../store/advertise/api/interface';
import { FormikProvider, useFormik } from 'formik';
import {
  createAdvertise,
  getAdvertise,
  patchAdvertise,
} from '../../../store/advertise/api';
import { setStatus } from '../../../store/advertise/advertiseSlice';
import { useEffect, useRef, useState, useCallback } from 'react';
import { decryptString } from '../../../utils/url';
import Loader from '../../../components/Loader';
import { omit, uniqBy } from 'lodash';
import { toast } from 'react-toastify';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';
import {
  findSelectPropertyCategory,
  searchSelectPropertyCategory,
} from '../../../store/selectPropertyCategory/api';
import {
  setSkip as setSkipPropertyCategory,
  reset as resetPropertyCategory,
} from '../../../store/selectPropertyCategory/selectPropertyCategorySlice';
import { DocumentType, PropertyPossessionType } from '../../../enum';
import {
  findSelectPropertySubCategory,
  searchSelectPropertySubCategory,
} from '../../../store/selectPropertySubCategory/api';
import {
  setSkip as setSkipPropertySubCategory,
  reset as resetPropertySubCategory,
  setStatus as setSelectStatusPropertySubCategory,
} from '../../../store/selectPropertySubCategory/selectPropertySubCategorySlice';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import useToggle from '../../../hooks/useToggle';
import UploadFormModal from '../../../components/UploadAssetsModal';
import { getAttachmentAssetUrl, isImageMediaType } from '../../../utils/url';
import { removeUploadAssets } from '../../../utils/removeUploadAssetsApiCall';
import CloseIcon from '@mui/icons-material/Close';
import companyAdvertiseSchema from '../../../validation/companyAdvertiseSchema';
import {
  MapContainer,
  Marker,
  TileLayer,
  Polygon,
  useMap,
} from 'react-leaflet';
import { LatLngExpression, LatLngTuple } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { customIcon } from '../../../utils/icon';
import {
  findSelectPossessionType,
  searchSelectPossessionType,
} from '../../../store/selectPossessionType/api';
import {
  reset as resetPossessionType,
  setSkip as setSkipPossessionType,
} from '../../../store/selectPossessionType/selectPossessionTypeSlice';
import MultipleAutoComplete from '../../../components/MultipleAutoComplete';
import {
  findSelectPropertyFacility,
  searchSelectPropertyFacility,
} from '../../../store/selectPropertyFacility/api';
import { PropertyFacility } from '../../../store/propertyFacility/api/interface';
import {
  reset as resetPropertyFacility,
  setSkip as setSkipPropertyFacility,
  setStatus as setSelectStatusPropertyFacility,
} from '../../../store/selectPropertyFacility/selectPropertyFacilitySlice';
import DetailsForm from '../../Advertise/Form/DetailsForm';
import {
  findSelectLocationManagement,
  searchSelectLocationManagement,
} from '../../../store/selectLocationManagement/api';
import {
  reset as resetLocation,
  setSkip as setSkipLocation,
} from '../../../store/selectLocationManagement/selectLocationManagementSlice';
import { findLocationCoordinate } from '../../../store/getLocationCoordinate/api';
// PinImage no longer needed - using Leaflet customIcon
import { RENT_FREQUENCY } from '../../../constants';
import {
  setStatus as setSelectStatusPropertyFeature,
  reset as resetSelectPropertyFeature,
} from '../../../store/selectPropertyFeature/selectPropertyFeatureSlice';

// Keep libraries array outside component to prevent re-renders
// Leaflet doesn't require API key or libraries

const CompanyAdvertiseForm = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const params: any = useParams();

  const isAddMode = !params?.id;
  const { isLoading } = useAppSelector((state) => state.advertise);
  const mapRef: any = useRef(null);
  const [response, setResponse] = useState<any>();
  const [attachmentArray, setAttachmentArray] = useState<string[]>([]);
  const [isLand, setIsLand] = useState(false);
  const [polygonCoordinates, setPolygonCoordinates] = useState<LatLngExpression[]>([]);
  const [zoom, setZoom] = useState<number>(9);
  const [mapCoordinates, setMapCoordinates] = useState<LatLngTuple | null>(null);
  const [hasLocation, setHasLocation] = useState<boolean>(false);
  const [approximatePolygon, setApproximatePolygon] = useState<LatLngExpression[]>([]);
  const [propertyFacility, setPropertyFacility] = useState<PropertyFacility[]>(
    []
  );
  const [selectedFurnitureFacility, setSelectedFurnitureFacility] = useState<PropertyFacility | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const { toggle, onOpen, onClose } = useToggle();
  const { accessToken } = useAppSelector((state) => state.auth);
  const [openLightBox, setOpenLightBox] = useState(false);
  const [activeLightBoxIndex, setActiveLightBoxIndex] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [displayedAttachments, setDisplayedAttachments] = useState<any[]>([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);

  // Leaflet uses customIcon from utils/icon.ts

  // Hard refresh when entering edit mode (only on navigation, not after reload)
  useEffect(() => {
    if (!isAddMode && params?.id) {
      const refreshKey = `edit_refresh_${params.id}`;
      const hasRefreshed = sessionStorage.getItem(refreshKey);
      
      // Only refresh if we haven't already refreshed for this edit session
      // This prevents infinite loops while allowing refresh on each new navigation
      if (!hasRefreshed) {
        // Mark as refreshed before reloading to prevent infinite loop
        sessionStorage.setItem(refreshKey, 'true');
        // Hard refresh the entire page
        window.location.reload();
        return; // Exit early to prevent further execution
      }
    }

    // Cleanup: clear refresh flag when component unmounts (user navigates away)
    return () => {
      if (!isAddMode && params?.id) {
        const refreshKey = `edit_refresh_${params.id}`;
        sessionStorage.removeItem(refreshKey);
      }
    };
  }, [isAddMode, params?.id]);

  const formik = useFormik<CreatePatchAdvertise>({
    initialValues: {
      id: '',
      title: '',
      areaPolygon: '',
      description: '',
      zipCode: '',
      zoneNumber: '',
      streetNumber: '',
      buildingNumber: '',
      address: '',
      latitude: 0,
      longitude: 0,
      possessionTypeId: '',
      rentFrequency: '',
      price: '',
      status: 'submit', // Company admin ads should go for approval
      categoryId: '',
      subCategoryId: '',
      userId: '', // Will be set automatically from logged-in user
      locationId: '',
      attachmentArray: [],
      propertyFacilityIds: [],
      propertyFeatureItems: [
        {
          featureId: '',
          value: '',
        },
      ],
      isApproximateLocation: false,
    },
    validationSchema: companyAdvertiseSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      console.log('Form submission started', values);
      console.log('Form errors:', formik?.errors);
      console.log('Form touched:', formik?.touched);
      
      // Check for required fields and show alerts
      const requiredFields = [
        { field: 'title', label: 'Title' },
        { field: 'description', label: 'Description' },
        { field: 'price', label: 'Price' },
        { field: 'categoryId', label: 'Category' },
        { field: 'subCategoryId', label: 'Sub Category' },
        { field: 'possessionTypeId', label: 'Possession Type' },
        { field: 'locationId', label: 'Zone' },
        { field: 'attachmentArray', label: 'Images' },
        { field: 'propertyFacilityIds', label: 'Property Facility' },
      ];

      // Check if rent frequency is required (for rent properties)
      if (values.possessionTypeId === PropertyPossessionType.forRent && !values.rentFrequency) {
        toast.error('Rent Frequency is required');
        setFieldError('rentFrequency', 'Rent Frequency is required');
        setSubmitting(false);
        return;
      }

      // Check required fields
      const missingFields: string[] = [];
      for (const { field, label } of requiredFields) {
        const value = (values as any)[field];
        if (!value || (Array.isArray(value) && value.length === 0)) {
          missingFields.push(label);
        }
      }

      // Check property features
      if (!values.propertyFeatureItems || values.propertyFeatureItems.length === 0) {
        missingFields.push('Property Features');
      } else {
        const invalidFeatures = values.propertyFeatureItems.filter(
          (item: any) => !item.featureId || !item.value
        );
        if (invalidFeatures.length > 0) {
          missingFields.push('Property Features (all features must have values)');
        }
      }

      // Show alert if any fields are missing
      if (missingFields.length > 0) {
        const message = `Please fill in the following required fields: ${missingFields.join(', ')}`;
        toast.error(message);
        setSubmitting(false);
        return;
      }

      // Check location coordinates
      if (!values.latitude || !values.longitude || values.latitude === 0 || values.longitude === 0) {
        toast.error('Please click "Get Address" to set the location coordinates');
        setSubmitting(false);
        return;
      }
      
      // Ensure furniture facility is included in propertyFacilityIds
      const furnitureId = selectedFurnitureFacility?.id;
      const currentFacilityIds = values.propertyFacilityIds || [];
      const furnitureIds = furnitureFacilities.map(f => f.id);
      const regularFacilityIds = currentFacilityIds.filter(id => !furnitureIds.includes(id));
      const finalFacilityIds = furnitureId 
        ? [...regularFacilityIds, furnitureId]
        : regularFacilityIds;

      try {
        if (isAddMode) {
          const reqBody: any = omit(values, ['id']); // Keep userId but it will be set from logged-in user on backend
          reqBody.propertyFacilityIds = finalFacilityIds; // Include furniture facility
          if (reqBody.areaPolygon === '') {
            delete reqBody.areaPolygon;
          }
          // Remove userId - backend will use logged-in user
          delete reqBody.userId;
          // Set status to 'submit' for company admin ads (goes for approval)
          reqBody.status = 'submit';
          console.log('Creating advertise with body:', reqBody);
          await dispatch(createAdvertise(reqBody)).unwrap();
          console.log('Advertise created successfully');
        }
        if (!isAddMode) {
          const reqBody: any = { ...values, propertyFacilityIds: finalFacilityIds };
          delete reqBody.createdAt;
          delete reqBody.createdBy;
          delete reqBody.updatedAt;
          delete reqBody.updatedBy;
          delete reqBody.property_category;
          delete reqBody.userId; // Remove userId for company admin
          
          // Remove empty strings and convert to null for optional fields
          if (reqBody.areaPolygon === '') {
            delete reqBody.areaPolygon;
          }
          if (reqBody.zipCode === '') {
            delete reqBody.zipCode;
          }
          if (reqBody.address === '') {
            delete reqBody.address;
          }
          if (reqBody.rentFrequency === '') {
            delete reqBody.rentFrequency;
          }
          
          // Ensure id is present for patch
          if (!reqBody.id) {
            throw new Error('Advertise ID is required for update');
          }
          
          console.log('Updating advertise with body:', reqBody);
          await dispatch(patchAdvertise(reqBody)).unwrap();
          console.log('Advertise updated successfully');
        }
        formik?.resetForm();
        dispatch(setStatus({ status: 'idle' }));
        navigation('/company-advertise');
      } catch (error: any) {
        console.error('Error submitting form:', error);
        // Show validation errors as alerts
        if (error?.errors) {
          console.error('Validation errors:', error.errors);
          // If it's an object with field-specific errors
          if (typeof error.errors === 'object') {
            const errorMessages = Object.values(error.errors).filter(Boolean);
            if (errorMessages.length > 0) {
              const firstError = Array.isArray(errorMessages[0]) 
                ? errorMessages[0][0] 
                : errorMessages[0];
              toast.error(firstError || 'Please fill in all required fields');
            }
          } else {
            toast.error(error.errors || 'Please fill in all required fields');
          }
        } else if (error?.message) {
          toast.error(error.message);
        } else {
          toast.error('Please fill in all required fields');
        }
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchById = async () => {
      if (isAddMode) {
        setIsDataLoaded(false);
        setIsFetchingData(false);
        return;
      }

      // Reset state when entering edit mode to ensure fresh data load
      setIsDataLoaded(false);
      setIsFetchingData(true);
      setResponse(null);
      lastPopulatedResponseId.current = null;

      // Always fetch from API when editing to ensure we have complete data
      // State might not have all nested relationships (facilities, features, etc.)
      try {
        const decodeParam = decryptString(params.id);
        const record = await dispatch(
          getAdvertise({ id: decodeParam })
        ).unwrap();
        
        // Wait a bit to ensure nested data is fully loaded
        await new Promise(resolve => setTimeout(resolve, 300));
        
        if (record?.latitude !== 0 && record?.longitude !== 0) {
          setMapCoordinates([record.latitude, record.longitude]);
          setHasLocation(true);
        }
        setResponse(record);
        setIsDataLoaded(true);
        setIsFetchingData(false);
      } catch (error: any) {
        setIsFetchingData(false);
        navigation('/company-advertise');
      }
    };
    fetchById();
  }, [dispatch, isAddMode, navigation, params?.id]);

  // Populate form values when response is loaded (for edit mode)
  const lastPopulatedResponseId = useRef<string | null>(null);
  const populateForm = useCallback(() => {
    if (!response || !response.id || isAddMode) return;
    
    // Simple check - only populate once per response ID
    if (lastPopulatedResponseId.current === response.id) return;
    lastPopulatedResponseId.current = response.id;
    
    // Extract facility IDs - simple extraction, ensure it's always an array
    const facilityIds = (response.advertise_property_facility_mappers || [])
      .map((mapper: any) => mapper.facilityId)
      .filter((id: any) => id);
    
    // Ensure propertyFacilityIds is always an array (never undefined/null)
    const safeFacilityIds = Array.isArray(facilityIds) ? facilityIds : [];
    
    // Extract feature items - simple extraction
    const featureItems = (response.advertise_property_feature_mappers || [])
      .map((mapper: any) => ({
        featureId: mapper.featureId || '',
        value: mapper.value ? String(mapper.value) : '',
      }))
      .filter((item: any) => item.featureId);
    
    // Set basic fields
    const formValues = {
      id: response.id || '',
      title: response.title || '',
      areaPolygon: response.areaPolygon || '',
      description: response.description || '',
      zipCode: response.zipCode || '',
      streetNumber: response.streetNumber || '',
      buildingNumber: response.buildingNumber || '',
      address: response.address || '',
      latitude: response.latitude || 0,
      longitude: response.longitude || 0,
      possessionTypeId: response.possessionTypeId || '',
      rentFrequency: response.rentFrequency || '',
      price: response.price || '',
      status: response.status || 'draft', // Preserve existing status
      categoryId: response.categoryId || '',
      subCategoryId: response.subCategoryId || '',
      userId: response.userId || '', // Will be removed before submission
      locationId: response.locationId || '',
      zoneNumber: response.location_management?.code || response.zoneNumber || '',
      attachmentArray: response.attachments?.map((att: any) => att.id) || [],
      propertyFacilityIds: safeFacilityIds,
      propertyFeatureItems: featureItems.length > 0 ? featureItems : [
        {
          featureId: '',
          value: '',
        },
      ],
      isApproximateLocation: response.isApproximateLocation || false,
    };
    
    // Ensure at least one empty property feature item if array is empty
    if (!formValues.propertyFeatureItems || formValues.propertyFeatureItems.length === 0) {
      formValues.propertyFeatureItems = [
        {
          featureId: '',
          value: '',
        },
      ];
    }
    
    formik?.setValues(formValues, false); // false = don't validate on set

    // Set attachment array for upload modal
    if (response.attachments && response.attachments.length > 0) {
      setAttachmentArray(response.attachments.map((att: any) => att.id));
      setDisplayedAttachments(response.attachments);
    }

    // Set property facility for display - based on actual API response structure
    // The API returns property_facility nested object with full details
    if (response.advertise_property_facility_mappers && response.advertise_property_facility_mappers.length > 0) {
      const facilities = response.advertise_property_facility_mappers
        .map((mapper: any) => {
          // property_facility is the nested object with full facility details
          return mapper.property_facility || mapper.propertyFacility;
        })
        .filter((facility: any) => facility && facility.id); // Filter out null/undefined and ensure has id
      
      // Separate furniture facilities from regular facilities
      const furnitureFacilities = facilities.filter((facility: PropertyFacility) => 
        isFurnitureFacility(facility)
      );
      const regularFacilities = facilities.filter((facility: PropertyFacility) => 
        !isFurnitureFacility(facility)
      );
      
      // Set furniture facility (only one should be selected)
      if (furnitureFacilities.length > 0) {
        setSelectedFurnitureFacility(furnitureFacilities[0]);
      } else {
        setSelectedFurnitureFacility(null);
      }
      
      // Set regular facilities
      setPropertyFacility(regularFacilities);
    }

    // Check if category is land type
    if (response.property_category) {
      setIsLand(response.property_category.isLand || false);
    }

    // Handle polygon coordinates if exists
    if (response.areaPolygon) {
      // Parse polygon string and set coordinates
      try {
        const polygonMatch = response.areaPolygon.match(/POLYGON\(\((.*?)\)\)/);
        if (polygonMatch) {
          const coords: LatLngTuple[] = polygonMatch[1].split(',').map((coord: string) => {
            const [lng, lat] = coord.trim().split(' ');
            return [parseFloat(lat), parseFloat(lng)] as LatLngTuple;
          });
          // If approximate location is enabled, use approximate polygon, otherwise use regular polygon
          if (response.isApproximateLocation) {
            setApproximatePolygon(coords);
            setPolygonCoordinates([]);
          } else {
            setPolygonCoordinates(coords);
            setApproximatePolygon([]);
          }
        }
      } catch (e) {
        console.error('Error parsing polygon:', e);
      }
    }
  }, [response, isAddMode, formik]);

  // Handle approximate location checkbox change
  useEffect(() => {
    // When checking or unchecking: Clear all location point data
    // User must click "Get Address" again to set the correct location
    setApproximatePolygon([]);
    formik?.setFieldValue('latitude', 0);
    formik?.setFieldValue('longitude', 0);
    formik?.setFieldValue('areaPolygon', '');
    setMapCoordinates(null);
    setHasLocation(false);
    setPolygonCoordinates([]);
  }, [formik?.values.isApproximateLocation]);

  useEffect(() => {
    if (!response || !response.id || isAddMode || !isDataLoaded || isFetchingData) {
      if (isAddMode) {
        lastPopulatedResponseId.current = null;
      }
      return;
    }
    
    // Simple - just populate the form when data is loaded
    populateForm();
  }, [populateForm, isAddMode, response, isDataLoaded, isFetchingData]);

  useEffect(() => {
    formik?.setFieldValue('attachmentArray', attachmentArray);
  }, [attachmentArray]);

  // Keep create-mode previews from the upload modal. upload-asset find is
  // disallowed, so we cannot re-fetch metadata by id after confirm.
  const handleUploadedAttachments = useCallback(
    (
      attachments: Array<{
        id: string;
        key?: string;
        fileName?: string;
        fileType?: string;
        url?: string;
      }>
    ) => {
      // Store stable URLs (never blob:) so modal/form previews survive reopen
      setDisplayedAttachments(
        attachments.map((att) => {
          const key =
            att.key ||
            (att.id
              ? `advertise/${att.id}.${
                  att.fileName?.split('.').pop()?.toLowerCase() || 'jpg'
                }`
              : undefined);
          const stableUrl = getAttachmentAssetUrl({
            id: att.id,
            key,
            fileName: att.fileName,
            fileType: att.fileType,
          });
          return {
            ...att,
            key,
            url:
              stableUrl ||
              (att.url && !att.url.startsWith('blob:') ? att.url : undefined),
          };
        })
      );
    },
    []
  );

  useEffect(() => {
    if (response?.attachments && !isAddMode) {
      const fromResponse = response.attachments.filter((att: any) =>
        attachmentArray.includes(att.id)
      );
      setDisplayedAttachments((prev: any[]) => {
        const byId = new Map(fromResponse.map((att: any) => [att.id, att]));
        prev.forEach((att: any) => {
          if (attachmentArray.includes(att.id) && !byId.has(att.id)) {
            byId.set(att.id, att);
          }
        });
        return Array.from(byId.values()).filter((att: any) =>
          attachmentArray.includes(att.id)
        );
      });
      return;
    }

    if (attachmentArray.length === 0) {
      setDisplayedAttachments([]);
      return;
    }

    setDisplayedAttachments((prev: any[]) =>
      prev.filter((att: any) => attachmentArray.includes(att.id))
    );
  }, [attachmentArray, response, isAddMode]);

  const imageAttachments = displayedAttachments.filter((attachment) =>
    isImageMediaType(attachment.fileType, attachment.fileName)
  );

  const handleOpenLightBox = (idx: number) => {
    setOpenLightBox(true);
    setActiveLightBoxIndex(idx);
  };

  const handleCloseLightBox = () => {
    setRotation(0);
    setOpenLightBox(false);
    setActiveLightBoxIndex(0);
  };

  const handleNextImage = () => {
    setRotation(0);
    if (activeLightBoxIndex >= imageAttachments.length - 1) return;
    setActiveLightBoxIndex((curr) => curr + 1);
  };

  const handlePrvImage = () => {
    setRotation(0);
    if (activeLightBoxIndex === 0) return;
    setActiveLightBoxIndex((curr) => curr - 1);
  };

  const handleRotate = () => {
    setRotation((curr) => curr + 90);
  };

  const handleRemoveImage = async (attachmentId: string) => {
    try {
      const result = await removeUploadAssets(accessToken, attachmentId);
      if (result) {
        // Remove from attachmentArray
        const newAttachmentArray = attachmentArray.filter((id: string) => id !== attachmentId);
        setAttachmentArray(newAttachmentArray);
        
        // Remove from displayed attachments
        const newDisplayed = displayedAttachments.filter((att: any) => att.id !== attachmentId);
        setDisplayedAttachments(newDisplayed);
      }
    } catch (error) {
      console.error('Error removing image:', error);
    }
  };

  // Component to set map reference
  const SetMapRef = () => {
    const map = useMap();
    useEffect(() => {
      if (map) {
        mapRef.current = map;
      }
    }, [map]);
    return null;
  };

  // Utility function to generate circular polygon coordinates (200m radius)
  const generateCircularPolygon = (center: LatLngTuple, radiusMeters = 200): LatLngTuple[] => {
    const [centerLat, centerLng] = center;
    const points: LatLngTuple[] = [];
    const numPoints = 32; // Number of points to create a smooth circle
    
    // Earth's radius in meters
    const earthRadius = 6371000;
    
    for (let i = 0; i <= numPoints; i++) {
      const angle = (i * 360) / numPoints;
      const angleRad = (angle * Math.PI) / 180;
      
      // Calculate offset in meters
      const offsetLat = radiusMeters * Math.cos(angleRad) / earthRadius * (180 / Math.PI);
      const offsetLng = radiusMeters * Math.sin(angleRad) / (earthRadius * Math.cos(centerLat * Math.PI / 180)) * (180 / Math.PI);
      
      const lat = centerLat + offsetLat;
      const lng = centerLng + offsetLng;
      
      points.push([lat, lng]);
    }
    
    return points;
  };

  // Convert polygon coordinates to POLYGON string format for backend
  const polygonToBackendFormat = (coords: LatLngExpression[]): string => {
    const points = coords.map((coord) => {
      if (Array.isArray(coord)) {
        return `${coord[1]} ${coord[0]}`; // lng lat format for backend
      }
      return `${coord.lng} ${coord.lat}`;
    });
    return `POLYGON((${points.join(',')}))`;
  };

  // Component to handle map drag/move for approximate location and keep marker centered
  const ApproximateLocationHandler = () => {
    const map = useMap();
    
    useEffect(() => {
      if (formik?.values.isApproximateLocation && map && mapCoordinates) {
        // Keep marker at center by updating map center when it moves
        const updateCenter = () => {
          const center = map.getCenter();
          const currentCenter: LatLngTuple = [center.lat, center.lng];
          
          // Only update if center actually changed
          if (mapCoordinates[0] !== center.lat || mapCoordinates[1] !== center.lng) {
            setMapCoordinates(currentCenter);
            formik?.setFieldValue('latitude', center.lat);
            formik?.setFieldValue('longitude', center.lng);
            
            // Generate and set circular polygon
            const circlePolygon = generateCircularPolygon(currentCenter, 200);
            setApproximatePolygon(circlePolygon);
            
            // Update formik with polygon data
            const polygonString = polygonToBackendFormat(circlePolygon);
            formik?.setFieldValue('areaPolygon', polygonString);
          }
        };
        
        map.on('dragend', updateCenter);
        map.on('moveend', updateCenter);
        
        return () => {
          map.off('dragend', updateCenter);
          map.off('moveend', updateCenter);
        };
      }
    }, [map, formik?.values.isApproximateLocation, mapCoordinates]);
    
    return null;
  };

  const getAddress = async () => {
    try {
      if (isLand) {
        if (formik?.values?.zipCode !== '') {
          const areaPolygonResult = await dispatch(
            findLocationCoordinate({ zipCode: formik?.values?.zipCode })
          ).unwrap();
          if (areaPolygonResult?.features?.[0]?.geometry?.rings) {
            const coordinatesArray: string[] = [];

            for (const key in areaPolygonResult?.features?.[0]?.geometry?.rings) {
              const coordinates =
                areaPolygonResult?.features?.[0]?.geometry?.rings[key];

              for (let i = 0; i < coordinates.length; i++) {
                const coord = coordinates[i];
                coordinatesArray.push(`${coord[0]} ${coord[1]}`);
              }

              const formattedCoords: LatLngExpression[] = coordinates.map((coord: number[]) => [
                coord[1],
                coord[0],
              ] as LatLngTuple);
              setPolygonCoordinates(formattedCoords);
              const firstItemCoordinate = coordinates[0];
              const lat = firstItemCoordinate[1];
              const lng = firstItemCoordinate[0];
              formik?.setFieldValue('latitude', lat);
              formik?.setFieldValue('longitude', lng);
              const newCenter: LatLngTuple = [lat, lng];
              setMapCoordinates(newCenter);
              setHasLocation(true);
              if (mapRef?.current) {
                mapRef.current.setView(newCenter, 15);
              }
              setZoom(15);
              
              // If approximate location is enabled, generate circular polygon instead of using land polygon
              if (formik?.values.isApproximateLocation) {
                const circlePolygon = generateCircularPolygon(newCenter, 200);
                setApproximatePolygon(circlePolygon);
                const polygonString = polygonToBackendFormat(circlePolygon);
                formik?.setFieldValue('areaPolygon', polygonString);
              } else {
                // Use the land polygon from API
                formik?.setFieldValue(
                  'areaPolygon',
                  `POLYGON((${coordinatesArray.join(',')}))`
                );
                setApproximatePolygon([]);
              }
            }
          } else {
            toast.error('Invalid PIN Number. Please enter a correct PIN Number.');
          }
        } else {
          toast.error('Please enter PIN Number');
        }
      } else {
        if (
          formik?.values?.zoneNumber !== '' &&
          formik?.values?.buildingNumber !== '' &&
          formik?.values?.streetNumber !== ''
        ) {
          const areaLatLongResult = await dispatch(
            findLocationCoordinate({
              zoneNumber: formik?.values?.zoneNumber,
              buildingNumber: formik?.values?.buildingNumber,
              streetNumber: formik?.values?.streetNumber,
            })
          ).unwrap();
          if (areaLatLongResult?.features?.[0]?.geometry) {
            const lat = areaLatLongResult?.features?.[0]?.geometry?.y as number;
            const lng = areaLatLongResult?.features?.[0]?.geometry?.x as number;
            const newCenter: LatLngTuple = [lat, lng];
            if (mapRef?.current) {
              mapRef.current.setView(newCenter, 15);
            }
            setZoom(15);
            formik?.setFieldValue('latitude', lat);
            formik?.setFieldValue('longitude', lng);
            setMapCoordinates(newCenter);
            setHasLocation(true);
            
            // If approximate location is enabled, generate circular polygon
            if (formik?.values.isApproximateLocation) {
              const circlePolygon = generateCircularPolygon(newCenter, 200);
              setApproximatePolygon(circlePolygon);
              const polygonString = polygonToBackendFormat(circlePolygon);
              formik?.setFieldValue('areaPolygon', polygonString);
            } else {
              // Clear approximate polygon if not enabled
              setApproximatePolygon([]);
              formik?.setFieldValue('areaPolygon', '');
            }
          } else {
            toast.error('Invalid location details. Please check Zone Number, Building Number, and Street Number.');
          }
        } else {
          toast.error('Please enter Zone Number, Building Number, and Street Number');
        }
      }
    } catch (error: any) {
      toast.error('Location not found. Please verify and enter correct location details.');
    }
  };

  // Property Category Data
  const {
    data: propertyCategoryData,
    isLoading: isPropertyCategoryLoading,
    skip: propertyCategorySkip,
    hasMore: propertyCategoryHasMore,
    status: propertyCategoryStatus,
  } = useAppSelector((state) => state.selectPropertyCategory);

  useEffect(() => {
    if (propertyCategoryStatus === 'idle') {
      dispatch(
        findSelectPropertyCategory({
          limit: 10,
          skip: propertyCategorySkip,
          activeOnly: true,
        })
      );
    }
  }, [propertyCategorySkip, propertyCategoryStatus]);

  const onScrollFetchPropertyCategory = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      propertyCategoryHasMore
    ) {
      dispatch(setSkipPropertyCategory({ skip: propertyCategorySkip + 10 }));
      dispatch(
        findSelectPropertyCategory({
          limit: 10,
          skip: propertyCategorySkip + 10,
          ...(search && { search: search }),
          activeOnly: true,
        })
      );
    }
  };

  const searchPropertyCategory = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetPropertyCategory());
      return;
    }
    const params = {
      activeOnly: true,
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipPropertyCategory({ skip: 0 }));
    dispatch(searchSelectPropertyCategory(params));
  };

  const uniquePropertyCategoryData = uniqBy(
    [
      ...(response?.property_category ? [response?.property_category] : []),
      ...propertyCategoryData,
    ],
    'id'
  );

  // Property Sub Category Data
  const {
    data: propertySubCategoryData,
    isLoading: isPropertySubCategoryLoading,
    skip: propertySubCategorySkip,
    hasMore: propertySubCategoryHasMore,
    status: propertySubCategoryStatus,
  } = useAppSelector((state) => state.selectPropertySubCategory);

  useEffect(() => {
    if (formik?.values?.categoryId && propertySubCategoryStatus === 'idle') {
      dispatch(
        findSelectPropertySubCategory({
          categoryId: formik?.values?.categoryId,
          limit: 10,
          skip: propertySubCategorySkip,
          activeOnly: true,
        })
      );
    }
  }, [
    propertySubCategorySkip,
    formik?.values?.categoryId,
    propertySubCategoryStatus,
  ]);

  const onScrollFetchPropertySubCategory = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      propertySubCategoryHasMore
    ) {
      dispatch(
        setSkipPropertySubCategory({ skip: propertySubCategorySkip + 10 })
      );
      dispatch(
        findSelectPropertySubCategory({
          categoryId: formik?.values?.categoryId,
          limit: 10,
          skip: propertySubCategorySkip + 10,
          ...(search && { search: search }),
          activeOnly: true,
        })
      );
    }
  };

  const searchPropertySubCategory = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetPropertySubCategory());
      return;
    }
    const params = {
      activeOnly: true,
      categoryId: formik?.values?.categoryId,
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipPropertySubCategory({ skip: 0 }));
    dispatch(searchSelectPropertySubCategory(params));
  };

  const uniquePropertySubCategoryData = uniqBy(
    [
      ...propertySubCategoryData,
    ],
    'id'
  );

  // Possession Type Data
  const {
    data: possessionTypeData,
    isLoading: isPossessionTypeLoading,
    skip: possessionTypeSkip,
    hasMore: possessionTypeHasMore,
    status: possessionTypeStatus,
  } = useAppSelector((state) => state.selectPossessionType);

  useEffect(() => {
    if (possessionTypeStatus === 'idle') {
      dispatch(
        findSelectPossessionType({
          limit: 10,
          skip: possessionTypeSkip,
        })
      );
    }
  }, [possessionTypeSkip, possessionTypeStatus]);

  const onScrollFetchPossessionType = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      possessionTypeHasMore
    ) {
      dispatch(setSkipPossessionType({ skip: possessionTypeSkip + 10 }));
      dispatch(
        findSelectPossessionType({
          limit: 10,
          skip: possessionTypeSkip + 10,
          ...(search && { search: search }),
        })
      );
    }
  };

  const searchPossessionType = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetPossessionType());
      return;
    }
    const params = {
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipPossessionType({ skip: 0 }));
    dispatch(searchSelectPossessionType(params));
  };

  const uniquePossessionTypeData = uniqBy([...possessionTypeData], 'id');

  // Location Data
  const {
    data: locationData,
    isLoading: isLocationLoading,
    skip: locationSkip,
    hasMore: locationHasMore,
    status: locationStatus,
  } = useAppSelector((state) => state.selectLocationManagement);

  useEffect(() => {
    if (locationStatus === 'idle') {
      dispatch(
        findSelectLocationManagement({
          limit: 10,
          skip: locationSkip,
        })
      );
    }
    // Reset search loading when status changes to idle or success
    if (locationStatus === 'idle' || locationStatus === 'succeeded') {
      setIsSearchingLocation(false);
    }
  }, [locationSkip, locationStatus]);

  const onScrollFetchLocation = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      locationHasMore
    ) {
      dispatch(setSkipLocation({ skip: locationSkip + 10 }));
      dispatch(
        findSelectLocationManagement({
          limit: 10,
          skip: locationSkip + 10,
          ...(search && { search: search }),
        })
      );
    }
  };

  const searchLocation = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetLocation());
      setIsSearchingLocation(false);
      return;
    }
    setIsSearchingLocation(true);
    const params = {
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipLocation({ skip: 0 }));
    dispatch(searchSelectLocationManagement(params)).finally(() => {
      setIsSearchingLocation(false);
    });
  };

  const uniqueLocationData = uniqBy([...locationData], 'id');

  // Property Facility
  const {
    data: propertyFacilityData,
    isLoading: isPropertyFacilityLoading,
    skip: skipPropertyFacility,
    hasMore: hasMorePropertyFacility,
    status: propertyFacilityStatus,
  } = useAppSelector((state) => state.selectPropertyFacility);

  useEffect(() => {
    if (formik?.values?.categoryId && propertyFacilityStatus === 'idle') {
      dispatch(
        findSelectPropertyFacility({
          limit: 10,
          skip: skipPropertyFacility,
          propertyCategoryId: formik?.values?.categoryId,
          ...(formik?.values?.subCategoryId && {
            propertySubCategoryId: formik?.values?.subCategoryId,
          }),
          activeOnly: true,
        })
      );
    }
  }, [
    skipPropertyFacility,
    formik?.values?.categoryId,
    formik?.values?.subCategoryId,
    propertyFacilityStatus,
  ]);

  const onScrollFetchPropertyFacility = (
    event: React.UIEvent<HTMLElement, UIEvent>
  ) => {
    if (
      event.currentTarget.scrollTop + 300 >= event.currentTarget.scrollHeight &&
      hasMorePropertyFacility
    ) {
      dispatch(setSkipPropertyFacility({ skip: skipPropertyFacility + 10 }));
      dispatch(setSelectStatusPropertyFacility({ status: 'idle' }));
    }
  };

  const searchPropertyFacility = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      dispatch(resetPropertyFacility());
      return;
    }
    const params = {
      activeOnly: true,
      propertyCategoryId: formik?.values?.categoryId,
      ...(formik?.values?.subCategoryId && {
        propertySubCategoryId: formik?.values?.subCategoryId,
      }),
      ...(e.target.value && { limit: 50 }),
      ...(e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipPropertyFacility({ skip: 0 }));
    dispatch(searchSelectPropertyFacility(params));
  };

  // Helper function to check if a facility is a furniture option
  const isFurnitureFacility = (facility: PropertyFacility): boolean => {
    const labelEn = (facility.labelEn || '').toLowerCase();
    const labelAr = (facility.labelAr || '').toLowerCase();
    
    return (
      labelEn.includes('unfurnished') ||
      labelEn.includes('semi-furnished') ||
      labelEn.includes('fully-furnished') ||
      labelEn.includes('semi furnished') ||
      labelEn.includes('fully furnished') ||
      labelAr.includes('unfurnished') ||
      labelAr.includes('semi-furnished') ||
      labelAr.includes('fully-furnished') ||
      labelAr.includes('semi furnished') ||
      labelAr.includes('fully furnished')
    );
  };

  // Separate furniture facilities from regular facilities
  const allPropertyFacilityData = uniqBy(
    [
      ...(propertyFacility?.length > 0 ? [...propertyFacility] : []),
      ...propertyFacilityData,
    ],
    'id'
  );

  // Furniture facilities (for separate field)
  const furnitureFacilities = allPropertyFacilityData.filter((facility) => 
    isFurnitureFacility(facility)
  );

  // Regular facilities (excluding furniture)
  const uniquePropertyFacilityData = allPropertyFacilityData.filter((facility) => 
    !isFurnitureFacility(facility)
  );

  return (
    <Grid
      container
      spacing={3}
      mt={1.5}
    >
      <Grid
        item
        xs={12}
        md={12}
      >
        {(isLoading || isFetchingData || (!isDataLoaded && !isAddMode)) ? (
          <Loader />
        ) : (
          <FormikProvider value={formik}>
            <form 
              onSubmit={async (e) => {
                e.preventDefault();
                console.log('Form onSubmit event triggered');
                console.log('Formik instance:', formik);
                if (formik) {
                  // Validate form before submission
                  const errors = await formik.validateForm();
                  if (Object.keys(errors).length > 0) {
                    // Show alert for validation errors
                    const errorMessages = Object.values(errors).filter(Boolean);
                    if (errorMessages.length > 0) {
                      let firstError = 'Please fill in all required fields';
                      const firstErrorValue = errorMessages[0];
                      if (typeof firstErrorValue === 'string') {
                        firstError = firstErrorValue;
                      } else if (Array.isArray(firstErrorValue)) {
                        firstError = (firstErrorValue[0] as string) || firstError;
                      } else if (firstErrorValue && typeof firstErrorValue === 'object') {
                        // Handle nested object errors
                        const nestedError = Object.values(firstErrorValue).find((v) => typeof v === 'string');
                        firstError = (nestedError as string) || firstError;
                      }
                      toast.error(firstError);
                    } else {
                      toast.error('Please fill in all required fields');
                    }
                    // Mark all fields as touched to show errors
                    Object.keys(errors).forEach((key) => {
                      formik.setFieldTouched(key, true);
                    });
                    return;
                  }
                  formik.handleSubmit(e);
                } else {
                  console.error('Formik is not initialized');
                  toast.error('Form is not initialized. Please refresh the page.');
                }
              }}
            >
              <Card sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                    },
                  }}
                >
                  <TextField
                    fullWidth
                    label={'Title'}
                    name='title'
                    id='title'
                    onChange={formik?.handleChange}
                    value={formik?.values.title}
                    error={Boolean(
                      formik?.errors.title && formik?.touched.title
                    )}
                    helperText={
                      Boolean(formik?.errors.title && formik?.touched.title) &&
                      formik?.errors.title
                    }
                  />

                  <TextField
                    fullWidth
                    label={'Description'}
                    name='description'
                    id='description'
                    onChange={formik?.handleChange}
                    value={formik?.values.description}
                    error={Boolean(
                      formik?.errors.description && formik?.touched.description
                    )}
                    helperText={
                      Boolean(
                        formik?.errors.description &&
                          formik?.touched.description
                      ) && formik?.errors.description
                    }
                  />

                  <AutoComplete
                    key={`procat-${formik?.values?.categoryId}`}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'string') {
                      } else if (newValue) {
                        setIsLand(newValue.isLand);
                        dispatch(resetPropertySubCategory());
                        dispatch(
                          setSelectStatusPropertySubCategory({ status: 'idle' })
                        );
                        dispatch(resetPropertyFacility());
                        dispatch(
                          setSelectStatusPropertyFacility({ status: 'idle' })
                        );
                        dispatch(resetSelectPropertyFeature());
                        dispatch(
                          setSelectStatusPropertyFeature({ status: 'idle' })
                        );
                        formik?.setFieldValue('propertyFeatureItems', [
                          {
                            featureId: '',
                            value: '',
                          },
                        ]);
                        setPropertyFacility([]);
                        formik?.setFieldValue('propertyFacilityIds', []);
                        formik?.setFieldValue('categoryId', newValue.id);
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      if (!newInputValue) {
                        dispatch(resetPropertyFacility());
                        dispatch(
                          setSelectStatusPropertyFacility({ status: 'idle' })
                        );
                        dispatch(resetSelectPropertyFeature());
                        dispatch(
                          setSelectStatusPropertyFeature({ status: 'idle' })
                        );
                        formik?.setFieldValue('categoryId', '');
                        setIsLand(false);
                        formik?.setFieldValue('zipCode', '');
                        formik?.setFieldValue('locationId', '');
                        formik?.setFieldValue('zoneNumber', '');
                        formik?.setFieldValue('buildingNumber', '');
                        formik?.setFieldValue('streetNumber', '');
                        formik?.setFieldValue('propertyFeatureItems', [
                          {
                            featureId: '',
                            value: '',
                          },
                        ]);
                        setPropertyFacility([]);
                        formik?.setFieldValue('propertyFacilityIds', []);
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>{option.labelEn}</li>
                    )}
                    getOptionLabel={(option) => option.labelEn}
                    error={Boolean(
                      formik?.touched.categoryId && formik?.errors.categoryId
                    )}
                    helperText={
                      Boolean(
                        formik?.touched.categoryId && formik?.errors.categoryId
                      )
                        ? formik?.errors.categoryId
                        : ''
                    }
                    fetchFunction={onScrollFetchPropertyCategory}
                    label={'Select Property Category'}
                    loading={isPropertyCategoryLoading}
                    options={uniquePropertyCategoryData}
                    searchFunction={searchPropertyCategory}
                    value={
                      uniquePropertyCategoryData?.filter(
                        (category) => category.id === formik?.values.categoryId
                      )?.[0] || null
                    }
                  />

                  <AutoComplete
                    key={`prosubcat-${formik?.values?.subCategoryId}`}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'string') {
                      } else if (newValue) {
                        dispatch(resetPropertyFacility());
                        dispatch(
                          setSelectStatusPropertyFacility({ status: 'idle' })
                        );
                        dispatch(resetSelectPropertyFeature());
                        dispatch(
                          setSelectStatusPropertyFeature({ status: 'idle' })
                        );
                        formik?.setFieldValue('propertyFeatureItems', [
                          {
                            featureId: '',
                            value: '',
                          },
                        ]);
                        setPropertyFacility([]);
                        formik?.setFieldValue('propertyFacilityIds', []);
                        formik?.setFieldValue('subCategoryId', newValue.id);
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      if (!newInputValue) {
                        dispatch(resetPropertyFacility());
                        dispatch(
                          setSelectStatusPropertyFacility({ status: 'idle' })
                        );
                        dispatch(resetSelectPropertyFeature());
                        dispatch(
                          setSelectStatusPropertyFeature({ status: 'idle' })
                        );
                        formik?.setFieldValue('subCategoryId', '');
                        setPropertyFacility([]);
                        formik?.setFieldValue('propertyFacilityIds', []);
                        formik?.setFieldValue('propertyFeatureItems', [
                          {
                            featureId: '',
                            value: '',
                          },
                        ]);
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>{option.labelEn}</li>
                    )}
                    getOptionLabel={(option) => option.labelEn}
                    error={Boolean(
                      formik?.touched.subCategoryId &&
                        formik?.errors.subCategoryId
                    )}
                    helperText={
                      Boolean(
                        formik?.touched.subCategoryId &&
                          formik?.errors.subCategoryId
                      )
                        ? formik?.errors.subCategoryId
                        : ''
                    }
                    fetchFunction={onScrollFetchPropertySubCategory}
                    label={'Select Property Sub Category'}
                    loading={isPropertySubCategoryLoading}
                    options={uniquePropertySubCategoryData}
                    searchFunction={searchPropertySubCategory}
                    value={
                      uniquePropertySubCategoryData?.filter(
                        (subCategory) =>
                          subCategory.id === formik?.values.subCategoryId
                      )?.[0] || null
                    }
                  />

                  {/* User field removed for company admin - userId will be set automatically from logged-in user */}

                  <AutoComplete
                    key={`protype-${formik?.values?.possessionTypeId}`}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'string') {
                      } else if (newValue) {
                        formik?.setFieldValue('possessionTypeId', newValue.id);
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      if (!newInputValue) {
                        formik?.setFieldValue('possessionTypeId', '');
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>{option.labelEn}</li>
                    )}
                    getOptionLabel={(option) => option.labelEn}
                    error={Boolean(
                      formik?.touched.possessionTypeId &&
                        formik?.errors.possessionTypeId
                    )}
                    helperText={
                      Boolean(
                        formik?.touched.possessionTypeId &&
                          formik?.errors.possessionTypeId
                      )
                        ? formik?.errors.possessionTypeId
                        : ''
                    }
                    fetchFunction={onScrollFetchPossessionType}
                    label={'Select Possession Type'}
                    loading={isPossessionTypeLoading}
                    options={uniquePossessionTypeData}
                    searchFunction={searchPossessionType}
                    value={
                      uniquePossessionTypeData?.filter(
                        (possessionType) =>
                          possessionType.id === formik?.values.possessionTypeId
                      )?.[0] || null
                    }
                  />

                  <TextField
                    fullWidth
                    label={'Price'}
                    name='price'
                    id='price'
                    onChange={formik?.handleChange}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    value={formik?.values.price}
                    error={Boolean(
                      formik?.errors.price && formik?.touched.price
                    )}
                    helperText={
                      Boolean(formik?.errors.price && formik?.touched.price) &&
                      formik?.errors?.price
                    }
                  />

                  {formik?.values?.possessionTypeId ===
                    PropertyPossessionType.forRent && (
                    <FormControl
                      fullWidth
                      error={Boolean(
                        formik?.touched?.rentFrequency &&
                          formik?.errors?.rentFrequency
                      )}
                    >
                      <InputLabel>{'Rent Frequency'}</InputLabel>
                      <Select
                        name='rentFrequency'
                        labelId='rentFrequency'
                        id='rentFrequency'
                        value={formik?.values.rentFrequency}
                        label={'Rent Frequency'}
                        onChange={(e) =>
                          formik?.setFieldValue('rentFrequency', e.target.value)
                        }
                        MenuProps={{
                          style: {
                            maxHeight: 400,
                          },
                        }}
                      >
                        {RENT_FREQUENCY?.map((rentFrequency) => (
                          <MenuItem
                            key={rentFrequency.value}
                            value={rentFrequency.value}
                          >
                            {rentFrequency.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {Boolean(
                        formik?.touched.rentFrequency &&
                          formik?.errors.rentFrequency
                      ) && (
                        <FormHelperText>
                          {formik?.errors?.rentFrequency}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                </Box>

                <Box py={2}>
                  <MultipleAutoComplete
                    key={`proFac-${uniquePropertyFacilityData?.length}`}
                    onChange={(event, newValue, reason) => {
                      if (reason === 'removeOption') {
                        // Normal removal - allow deselecting any facility
                        const updatedIds = newValue?.map(
                          (propertyCategory) => propertyCategory.id
                        ) || [];
                        
                        // Include furniture facility ID if selected
                        const furnitureId = selectedFurnitureFacility?.id;
                        const finalIds = furnitureId 
                          ? [...updatedIds, furnitureId]
                          : updatedIds;
                        
                        formik?.setFieldValue('propertyFacilityIds', finalIds);
                        setPropertyFacility((propertyFacilityInfo) =>
                          propertyFacilityInfo.filter(
                            (propertyFacility) =>
                              propertyFacility?.id === newValue[0]?.id
                          )
                        );
                      }
                      if (typeof newValue === 'string') {
                        // Handle string input (shouldn't happen with MultipleAutoComplete)
                      } else if (Array.isArray(newValue)) {
                        // Normal multiple selection for non-furniture facilities
                        const regularFacilityIds = newValue?.map(
                          (propertyFacility) => propertyFacility?.id
                        ) || [];
                        
                        // Include furniture facility ID if selected
                        const furnitureId = selectedFurnitureFacility?.id;
                        const finalIds = furnitureId 
                          ? [...regularFacilityIds, furnitureId]
                          : regularFacilityIds;
                        
                        formik?.setFieldValue('propertyFacilityIds', finalIds);
                        setPropertyFacility((curr) =>
                          uniqBy([...curr, ...newValue], 'id')
                        );
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      if (!newInputValue) {
                        // Keep furniture facility ID when clearing regular facilities
                        const furnitureId = selectedFurnitureFacility?.id;
                        formik?.setFieldValue('propertyFacilityIds', furnitureId ? [furnitureId] : []);
                      }
                    }}
                    renderOption={(props, option: PropertyFacility) => (
                      <li {...props}>{option.labelEn}</li>
                    )}
                    getOptionLabel={(option: PropertyFacility) =>
                      option.labelEn
                    }
                    fetchFunction={onScrollFetchPropertyFacility}
                    label={'Select Property Facilities'}
                    onFocus={() => dispatch(setStatus({ status: 'idle' }))}
                    loading={isPropertyFacilityLoading}
                    options={uniquePropertyFacilityData}
                    searchFunction={searchPropertyFacility}
                    value={uniquePropertyFacilityData.filter(
                      (item: PropertyFacility) =>
                        (formik?.values.propertyFacilityIds || []).includes(item.id)
                    )}
                    limitTags={4}
                    error={Boolean(
                      formik?.touched.propertyFacilityIds &&
                        formik?.errors.propertyFacilityIds
                    )}
                    helperText={
                      Boolean(
                        formik?.touched.propertyFacilityIds &&
                          formik?.errors.propertyFacilityIds
                      )
                        ? formik?.errors?.propertyFacilityIds
                        : ''
                    }
                  />
                </Box>

                <Box py={2}>
                  {/* Separate Furniture Selection Field */}
                  <AutoComplete<PropertyFacility>
                    key={`furniture-${furnitureFacilities?.length}`}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'string') {
                        // Handle string input
                      } else if (newValue && 'id' in newValue) {
                        // Furniture facility selected
                        const furnitureFacility = newValue as PropertyFacility;
                        setSelectedFurnitureFacility(furnitureFacility);
                        
                        // Update propertyFacilityIds: remove old furniture, add new one
                        const currentIds = formik?.values.propertyFacilityIds || [];
                        const furnitureIds = furnitureFacilities.map(f => f.id);
                        const nonFurnitureIds = currentIds.filter(id => !furnitureIds.includes(id));
                        const updatedIds = [...nonFurnitureIds, furnitureFacility.id];
                        
                        formik?.setFieldValue('propertyFacilityIds', updatedIds);
                      } else {
                        // Furniture facility deselected
                        setSelectedFurnitureFacility(null);
                        
                        // Remove furniture facility from propertyFacilityIds
                        const currentIds = formik?.values.propertyFacilityIds || [];
                        const furnitureIds = furnitureFacilities.map(f => f.id);
                        const updatedIds = currentIds.filter(id => !furnitureIds.includes(id));
                        
                        formik?.setFieldValue('propertyFacilityIds', updatedIds);
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      if (!newInputValue) {
                        setSelectedFurnitureFacility(null);
                        const currentIds = formik?.values.propertyFacilityIds || [];
                        const furnitureIds = furnitureFacilities.map(f => f.id);
                        const updatedIds = currentIds.filter(id => !furnitureIds.includes(id));
                        formik?.setFieldValue('propertyFacilityIds', updatedIds);
                      }
                    }}
                    renderOption={(props, option: PropertyFacility) => (
                      <li {...props}>{option.labelEn}</li>
                    )}
                    getOptionLabel={(option: PropertyFacility) =>
                      option.labelEn
                    }
                    label={'Select Furniture Type'}
                    loading={isPropertyFacilityLoading}
                    options={furnitureFacilities}
                    searchFunction={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      const inputEvent = e as React.ChangeEvent<HTMLInputElement>;
                      searchPropertyFacility(inputEvent);
                    }}
                    fetchFunction={onScrollFetchPropertyFacility}
                    value={selectedFurnitureFacility}
                  />
                </Box>

                <DetailsForm
                  isAddMode={isAddMode}
                  response={response}
                  values={formik.values}
                  handleChange={formik.handleChange}
                  setFieldValue={formik.setFieldValue}
                  errors={formik.errors}
                  touched={formik.touched}
                />

                <Box
                  sx={{
                    mt: 3,
                  }}
                >
                  <Typography
                    sx={{
                      mb: 2,
                      fontSize: '18px',
                    }}
                  >
                    Set Location:
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik?.values.isApproximateLocation || false}
                          onChange={(e) =>
                            formik?.setFieldValue('isApproximateLocation', e.target.checked)
                          }
                          name="isApproximateLocation"
                        />
                      }
                      label="Approximate Location"
                    />
                  </Box>
                  <>
                    <Grid
                        container
                        spacing={2}
                      >
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                        >
                          <AutoComplete
                            key={`toC-${formik?.values?.locationId}`}
                            onChange={(event, newValue) => {
                              if (typeof newValue === 'string') {
                              } else if (newValue) {
                                // Set both locationId and zoneNumber from the selected location
                                formik?.setFieldValue('locationId', newValue.id);
                                formik?.setFieldValue('zoneNumber', newValue.code || '');
                              } else {
                                // Clear when value is null
                                formik?.setFieldValue('locationId', '');
                                formik?.setFieldValue('zoneNumber', '');
                              }
                            }}
                            onInputChange={(event, newInputValue, reason) => {
                              // Only clear locationId and zoneNumber when input is cleared, not when selecting
                              if (reason === 'clear' || (reason === 'input' && !newInputValue)) {
                                formik?.setFieldValue('locationId', '');
                                formik?.setFieldValue('zoneNumber', '');
                              }
                            }}
                            renderOption={(props, option) => (
                              <li {...props}>{option.labelEn}</li>
                            )}
                            getOptionLabel={(option) => option.labelEn}
                            error={Boolean(
                              formik?.touched.locationId && formik?.errors.locationId
                            )}
                            helperText={
                              Boolean(
                                formik?.touched.locationId && formik?.errors.locationId
                              )
                                ? formik?.errors.locationId
                                : ''
                            }
                            fetchFunction={onScrollFetchLocation}
                            label={'Select Zone'}
                            loading={isLocationLoading || isSearchingLocation}
                            options={uniqueLocationData}
                            searchFunction={searchLocation}
                            value={
                              uniqueLocationData?.filter(
                                (location) => location.id === formik?.values.locationId
                              )?.[0] || null
                            }
                          />
                        </Grid>
                        {isLand ? (
                          <>
                            <Grid
                              item
                              lg={12}
                              md={12}
                              sm={12}
                              xs={12}
                            >
                              <TextField
                                fullWidth
                                label={'PIN Number'}
                                name='zipCode'
                                id='zipCode'
                                onChange={formik?.handleChange}
                                value={formik?.values.zipCode}
                                error={Boolean(
                                  formik?.errors.zipCode &&
                                    formik?.touched.zipCode
                                )}
                                helperText={
                                  Boolean(
                                    formik?.errors.zipCode &&
                                      formik?.touched.zipCode
                                  ) && formik?.errors.zipCode
                                }
                              />
                            </Grid>
                            <Grid
                              item
                              lg={8}
                              md={8}
                              sm={8}
                              xs={8}
                            ></Grid>
                            <Grid
                              item
                              lg={4}
                              md={4}
                              sm={4}
                              xs={4}
                            >
                              <Stack
                                direction={'row'}
                                display={'flex'}
                                justifyContent={'flex-end'}
                                alignItems='center'
                              >
                                <Button
                                  variant='contained'
                                  onClick={() => getAddress()}
                                >
                                  {'Get Address'}
                                </Button>
                              </Stack>
                            </Grid>
                          </>
                        ) : (
                          <>
                            <Grid
                              item
                              lg={4}
                              md={4}
                              sm={12}
                              xs={12}
                            >
                              <TextField
                                fullWidth
                                label={'Street Number'}
                                name='streetNumber'
                                id='streetNumber'
                                onChange={formik?.handleChange}
                                value={formik?.values.streetNumber}
                                error={Boolean(
                                  formik?.errors.streetNumber &&
                                    formik?.touched.streetNumber
                                )}
                                helperText={
                                  Boolean(
                                    formik?.errors.streetNumber &&
                                      formik?.touched.streetNumber
                                  ) && formik?.errors.streetNumber
                                }
                              />
                            </Grid>
                            <Grid
                              item
                              lg={4}
                              md={4}
                              sm={12}
                              xs={12}
                            >
                              <TextField
                                fullWidth
                                label={'Building Number'}
                                name='buildingNumber'
                                id='buildingNumber'
                                onChange={formik?.handleChange}
                                value={formik?.values.buildingNumber}
                                error={Boolean(
                                  formik?.errors.buildingNumber &&
                                    formik?.touched.buildingNumber
                                )}
                                helperText={
                                  Boolean(
                                    formik?.errors.buildingNumber &&
                                      formik?.touched.buildingNumber
                                  ) && formik?.errors.buildingNumber
                                }
                              />
                            </Grid>
                            <Grid
                              item
                              lg={8}
                              md={8}
                              sm={8}
                              xs={8}
                            ></Grid>
                            <Grid
                              item
                              lg={4}
                              md={4}
                              sm={4}
                              xs={4}
                            >
                              <Stack
                                direction={'row'}
                                display={'flex'}
                                justifyContent={'flex-end'}
                                alignItems='center'
                              >
                                <Button
                                  variant='contained'
                                  onClick={() => getAddress()}
                                >
                                  {'Get Address'}
                                </Button>
                              </Stack>
                            </Grid>
                          </>
                        )}
                      </Grid>
                      <Box sx={{ height: '400px', width: '100%', marginTop: '20px' }}>
                        <MapContainer
                          center={mapCoordinates || [25.3548, 51.1839]}
                          zoom={zoom}
                          style={{ height: '100%', width: '100%' }}
                        >
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          />
                          <SetMapRef />
                          {formik?.values.isApproximateLocation && <ApproximateLocationHandler />}
                          {hasLocation && mapCoordinates && (
                            <Marker
                              position={mapCoordinates}
                              icon={customIcon}
                            />
                          )}
                          {/* Show approximate polygon when approximate location is enabled */}
                          {formik?.values.isApproximateLocation && approximatePolygon.length > 0 && (
                            <Polygon
                              positions={approximatePolygon}
                              pathOptions={{
                                fillColor: '#a40466',
                                fillOpacity: 0.4,
                                color: '#a40466',
                                weight: 2,
                                opacity: 0.8,
                              }}
                            />
                          )}
                          {/* Show regular polygon when approximate location is disabled */}
                          {!formik?.values.isApproximateLocation && polygonCoordinates.length > 0 && (
                            <Polygon
                              positions={polygonCoordinates}
                              pathOptions={{
                                fillColor: '#a40466',
                                fillOpacity: 0.4,
                                color: '#a40466',
                                weight: 2,
                                opacity: 0.8,
                              }}
                            />
                          )}
                        </MapContainer>
                      </Box>
                  </>
                </Box>

                <Box sx={{ py: 3 }}>
                  <Button
                    variant='contained'
                    startIcon={<CloudUploadIcon />}
                    onClick={onOpen}
                  >
                    {'Upload Image'}
                  </Button>
                  {formik?.errors.attachmentArray &&
                    formik?.touched.attachmentArray && (
                      <p
                        className='upload-error-message'
                        style={{ marginTop: '10px' }}
                      >
                        {formik?.errors?.attachmentArray}
                      </p>
                    )}
                </Box>

                {/* Same Images section as company-advertise/view */}
                {imageAttachments.length > 0 && (
                  <Card sx={{ mt: 1, mb: 2 }}>
                    <CardHeader title={'Images'} />
                    <Box
                      sx={{
                        whiteSpace: 'normal',
                        m: 2,
                        maxHeight: 150,
                        overflow: 'auto',
                        display: 'flex',
                      }}
                    >
                      {imageAttachments.map((attachment, idx) => (
                        <div
                          key={attachment.id}
                          style={{
                            width: '100px',
                            height: '100px',
                            maxWidth: '100%',
                            borderRadius: 'unset',
                            margin: '8px',
                            position: 'relative',
                            flexShrink: 0,
                          }}
                        >
                          <Tooltip title={attachment?.fileName || 'Image'}>
                            <img
                              id={attachment.key}
                              alt={attachment?.fileName}
                              src={getAttachmentAssetUrl(
                                attachment,
                                accessToken
                              )}
                              loading='lazy'
                              onClick={() => handleOpenLightBox(idx)}
                              style={{
                                width: '100px',
                                height: '100px',
                                maxWidth: '100%',
                                borderRadius: 'unset',
                                position: 'relative',
                                cursor: 'pointer',
                                objectFit: 'cover',
                              }}
                            />
                          </Tooltip>
                          <IconButton
                            size='small'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveImage(attachment.id);
                            }}
                            sx={{
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              width: 22,
                              height: 22,
                              p: 0,
                              color: '#fff',
                              backgroundColor: 'rgba(22, 28, 36, 0.72)',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                              },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </div>
                      ))}
                    </Box>
                  </Card>
                )}

                <Stack
                  alignItems='center'
                  sx={{ mt: 3 }}
                  direction='row'
                  justifyContent='flex-end'
                  gap={1}
                >
                  <Button
                    variant='outlined'
                    onClick={() => navigation('/company-advertise')}
                  >
                    {'Cancel'}
                  </Button>

                  <LoadingButton
                    type='submit'
                    variant='contained'
                    loading={formik?.isSubmitting}
                  >
                    {isAddMode ? 'Add Advertise' : 'Edit Advertise'}
                  </LoadingButton>
                </Stack>
              </Card>
            </form>
          </FormikProvider>
        )}
      </Grid>

      <UploadFormModal
        toggle={toggle}
        onClose={onClose}
        setAttachmentArray={setAttachmentArray}
        module={DocumentType.Advertise}
        isSingleUpload={false}
        onUploadedAttachments={handleUploadedAttachments}
        existingAttachments={imageAttachments}
      />

      {/* Same lightbox as company-advertise/view */}
      {openLightBox && (
        <Box
          sx={{ pt: 'env(safe-area-inset-top) !important' }}
          id='myModal'
          className='modal'
        >
          <span className='close cursor'>
            <CloseIcon
              fontSize='large'
              onClick={handleCloseLightBox}
              sx={{ mt: 'env(safe-area-inset-top) !important' }}
            />
          </span>
          <div
            className={`modal-content ${
              openLightBox ? 'display-block ' : 'display-none'
            }`}
          >
            {imageAttachments?.[activeLightBoxIndex] && (
              <div className='vertical-center-div'>
                <div
                  className={`mySlides ${
                    openLightBox ? 'display-block ' : 'display-none'
                  }`}
                >
                  <img
                    id={imageAttachments?.[activeLightBoxIndex]?.key}
                    src={getAttachmentAssetUrl(
                      imageAttachments?.[activeLightBoxIndex],
                      accessToken
                    )}
                    alt={imageAttachments?.[activeLightBoxIndex]?.fileName}
                    style={{
                      transform: `rotate(${rotation}deg)`,
                      maxWidth: 'min(100%, 720px)',
                      maxHeight: 'min(80vh, 520px)',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                    }}
                  />
                </div>
              </div>
            )}
            <div
              className='prev'
              onClick={handlePrvImage}
            >
              <i className='fa-solid fa-arrow-left'></i>
            </div>
            <div
              className='next'
              onClick={handleNextImage}
            >
              <i className='fa-solid fa-arrow-right'></i>
            </div>
            <div
              className='rotate'
              onClick={handleRotate}
            >
              <i className='fa-solid fa-camera-rotate'></i>
            </div>
          </div>
        </Box>
      )}
    </Grid>
  );
};

export default CompanyAdvertiseForm;

